// Pure visual config -- no visibility state. Used by the table-driven canvas path.
export const OVERLAY_VISUAL_CONFIG = {
  vessel: { boxShadow: '0px 0px 2px 1px rgb(17, 87, 87), 0px 0px 3px rgb(27, 213, 166)' },
  birads2: { boxShadow: '0px 0px 2px 1px #155711, 0px 0px 3px #27d51b' },
  birads3: { boxShadow: '0px 0px 1px 1px rgb(139 88 13), 0px 0px 1px rgb(243 181 88)' },
  birads4: { boxShadow: '0px 0px 1px 1px rgb(137, 89, 16), 0px 0px 2px rgb(203, 152, 31)' },
  birads5: { boxShadow: '0px 0px 1px 1px rgb(147, 28, 28), 0px 0px 2px rgb(181, 70, 70)' },
  lesionKnown: { boxShadow: '0px 0px 1px 1px #04432e, 0px 0px 2px #1cff0d' },
};

// Legacy overlay type configuration (includes `show` state from overlay maps)
export const createMemoizedOverlayTypes = (
  microcalcOverlaysToShow,
  opacitiesOverlaysToShow,
  proj_name,
  theme
) => ({
  microcalcVessels: {
    lesionModule: 'microcalc',
    type: 'vessel',
    color: theme.palette.diagnosticsClasses.vessel,
    show: microcalcOverlaysToShow.vessel?.[proj_name?.toLowerCase()],
    boxShadow: '0px 0px 2px 1px rgb(17, 87, 87), 0px 0px 3px rgb(27, 213, 166)',
  },
  microcalcBirads2: {
    lesionModule: 'microcalc',
    type: 'birads2',
    color: theme.palette.diagnosticsClasses.birads2,
    show: microcalcOverlaysToShow.birads2?.[proj_name?.toLowerCase()],
    boxShadow: '0px 0px 2px 1px #155711, 0px 0px 3px #27d51b',
  },
  microcalcBirads3: {
    lesionModule: 'microcalc',
    type: 'birads3',
    color: theme.palette.diagnosticsClasses.birads3,
    show: microcalcOverlaysToShow.birads3?.[proj_name?.toLowerCase()],
    boxShadow: '0px 0px 1px 1px rgb(139 88 13), 0px 0px 1px rgb(243 181 88)',
  },
  microcalcBirads4: {
    lesionModule: 'microcalc',
    type: 'birads4',
    color: theme.palette.diagnosticsClasses.birads4,
    show: microcalcOverlaysToShow.birads4?.[proj_name?.toLowerCase()],
    boxShadow: '0px 0px 1px 1px rgb(137, 89, 16), 0px 0px 2px rgb(203, 152, 31)',
  },
  microcalcBirads5: {
    lesionModule: 'microcalc',
    type: 'birads5',
    color: theme.palette.diagnosticsClasses.birads5,
    show: microcalcOverlaysToShow.birads5?.[proj_name?.toLowerCase()],
    boxShadow: '0px 0px 1px 1px rgb(147, 28, 28), 0px 0px 2px rgb(181, 70, 70)',
  },
  lesionBirads2: {
    lesionModule: 'opacities',
    type: 'birads2',
    color: theme.palette.diagnosticsClasses.birads2,
    show: opacitiesOverlaysToShow.birads2?.[proj_name?.toLowerCase()],
    boxShadow: '0px 0px 2px 1px #155711, 0px 0px 3px #27d51b',
  },
  lesionBirads3: {
    lesionModule: 'opacities',
    type: 'birads3',
    color: theme.palette.diagnosticsClasses.birads3,
    show: opacitiesOverlaysToShow.birads3?.[proj_name?.toLowerCase()],
    boxShadow: '0px 0px 1px 1px rgb(139 88 13), 0px 0px 1px rgb(243 181 88)',
  },
  lesionBirads4: {
    lesionModule: 'opacities',
    type: 'birads4',
    color: theme.palette.diagnosticsClasses.birads4,
    show: opacitiesOverlaysToShow.birads4?.[proj_name?.toLowerCase()],
    boxShadow: '0px 0px 1px 1px rgb(137, 89, 16), 0px 0px 2px rgb(203, 152, 31)',
  },
  lesionBirads5: {
    lesionModule: 'opacities',
    type: 'birads5',
    color: theme.palette.diagnosticsClasses.birads5,
    show: opacitiesOverlaysToShow.birads5?.[proj_name?.toLowerCase()],
    boxShadow: '0px 0px 1px 1px rgb(147, 28, 28), 0px 0px 2px rgb(181, 70, 70)',
  },
  lesionKnown: {
    lesionModule: 'opacities',
    type: 'lesionKnown',
    color: theme.palette.diagnosticsClasses.lesionKnown,
    show: opacitiesOverlaysToShow.lesionKnown?.[proj_name?.toLowerCase()],
    boxShadow: '0px 0px 1px 1px #04432e, 0px 0px 2px #1cff0d',
  },
});

// Canvas drawing functions
export const drawSingleBoxOnCanvas = (box, context, color, lesionModule, show, imgSize, dashedMissed = false) => {
  if (!show || !box || !Array.isArray(box) || box.length !== 4) return;

  try {
    const [x, y, w, h] = box;
    const pixelX = x * imgSize[0];
    const pixelY = y * imgSize[1];
    const pixelW = w * imgSize[0];
    const pixelH = h * imgSize[1];

    context.globalCompositeOperation = 'source-over';
    context.strokeStyle = color || 'cyan';
    context.lineWidth = 2;
    context.setLineDash(dashedMissed ? [6, 4] : lesionModule === 'microcalc' ? [5, 5] : []);
    context.strokeRect(pixelX, pixelY, pixelW, pixelH);
    context.setLineDash([]);
  } catch (error) {
    console.error('Error in drawSingleBoxOnCanvas:', error);
  }
};

// Draw contour on canvas
export const drawContourOnCanvas = (contour, context, color, lesionModule, imgSize, dashedMissed = false) => {
  if (!contour || !Array.isArray(contour) || contour.length < 3) {
    return;
  }

  try {
    context.globalCompositeOperation = 'source-over';
    context.strokeStyle = color || 'cyan';
    context.lineWidth = 12;
    context.lineJoin = 'round';
    context.lineCap = 'round';
    context.setLineDash(dashedMissed ? [6, 4] : lesionModule === 'microcalc' ? [8, 8] : []);

    const points = contour.map(([x, y]) => [x * imgSize[0], y * imgSize[1]]);
    const smoothing = 0.1;

    // Catmull-Rom-like closed spline converted to cubic Bezier segments for smoother contours.
    context.beginPath();
    context.moveTo(points[0][0], points[0][1]);

    for (let i = 0; i < points.length; i++) {
      const p0 = points[(i - 1 + points.length) % points.length];
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      const p3 = points[(i + 2) % points.length];

      const cp1x = p1[0] + ((p2[0] - p0[0]) * smoothing) / 6;
      const cp1y = p1[1] + ((p2[1] - p0[1]) * smoothing) / 6;
      const cp2x = p2[0] - ((p3[0] - p1[0]) * smoothing) / 6;
      const cp2y = p2[1] - ((p3[1] - p1[1]) * smoothing) / 6;

      context.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2[0], p2[1]);
    }

    context.closePath();
    context.stroke();
    context.setLineDash([]);
  } catch (error) {
    console.error('Error in drawContourOnCanvas:', error);
  }
};

// Canvas initialization
export const initializeOffscreenCanvas = (imgSize, offscreenCanvasRef) => {
  if (!imgSize) return;

  const offscreenCanvas = document.createElement('canvas');
  offscreenCanvas.width = imgSize[0];
  offscreenCanvas.height = imgSize[1];
  offscreenCanvasRef.current = offscreenCanvas;

  // Initial clear with transparency
  const offscreenContext = offscreenCanvas.getContext('2d', { alpha: true });
  offscreenContext.clearRect(0, 0, imgSize[0], imgSize[1]);

  return () => {
    if (offscreenCanvasRef.current) {
      offscreenCanvasRef.current.width = 0;
      offscreenCanvasRef.current.height = 0;
      offscreenCanvasRef.current = null;
    }
  };
};

// Cleanup function
export const cleanupCanvasResources = (isDrawingRef, offscreenCanvasRef, pathPool) => {
  isDrawingRef.current = false;
  if (offscreenCanvasRef.current) {
    offscreenCanvasRef.current.width = 0;
    offscreenCanvasRef.current.height = 0;
    offscreenCanvasRef.current = null;
  }
  if (pathPool.current) {
    pathPool.current.clear();
  }
};

// Generate unique ID for rectangles
export const generateUniqueID = (proj_name, rect) => {
  let rectanglesLength = rect?.length || 0;
  let newID = rectanglesLength + proj_name;

  while (rect?.some((item) => item.id === newID)) {
    rectanglesLength++;
    newID = `0_${rectanglesLength}_${proj_name}`;
  }
  return newID;
};
